import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { StockLevel } from '../../types';
import { stockService } from '../../services/stockService';

const StockLevelsPage: React.FC = () => {
  const navigate = useNavigate();
  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    loadStockLevels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadStockLevels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await stockService.getStockLevels({
        pageNumber: currentPage,
        pageSize: pageSize,
      });

      if (response.data.data) {
        setStockLevels(response.data.data);
      }

      if (response.data.pagination) {
        setTotalPages(Math.ceil(response.data.pagination.total / pageSize));
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load stock levels');
      setLoading(false);
    }
  };

  const filteredStockLevels = stockLevels.filter((stock) => {
    const term = searchTerm.toLowerCase();
    return (
      (stock.productName?.toLowerCase().includes(term) || false) ||
      (stock.productSKU?.toLowerCase().includes(term) || false) ||
      (stock.warehouseName?.toLowerCase().includes(term) || false)
    );
  });

  const handleAdjustClick = (stockId: number, productId: number, warehouseId: number) => {
    navigate('/stock/adjust', {
      state: { stockId, productId, warehouseId },
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="container-fluid text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Stock Levels</h1>
          <div>
            <button className="btn btn-primary me-2" onClick={() => navigate('/stock/movements')}>
              View Movements
            </button>
            <button className="btn btn-success" onClick={() => navigate('/stock/adjust')}>
              + Adjust Stock
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="card mb-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search by product name, SKU, or warehouse..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Product SKU</th>
                  <th>Product Name</th>
                  <th>Warehouse</th>
                  <th className="text-center">Quantity</th>
                  <th className="text-center">Reserved</th>
                  <th className="text-center">Available</th>
                  <th className="text-center">Reorder Level</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStockLevels.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      No stock levels found
                    </td>
                  </tr>
                ) : (
                  filteredStockLevels.map((stock) => (
                    <tr key={stock.id}>
                      <td>
                        <code>{stock.productSKU}</code>
                      </td>
                      <td>{stock.productName}</td>
                      <td>{stock.warehouseName}</td>
                      <td className="text-center">{stock.quantity}</td>
                      <td className="text-center">{stock.reservedQuantity}</td>
                      <td className="text-center">
                        <strong>{stock.availableQuantity}</strong>
                      </td>
                      <td className="text-center">{stock.reorderLevel ?? 'N/A'}</td>
                      <td className="text-center">
                        {stock.quantity < (stock.reorderLevel ?? 0) ? (
                          <span className="badge bg-danger">Low</span>
                        ) : (
                          <span className="badge bg-success">OK</span>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleAdjustClick(stock.id, stock.productId, stock.warehouseId)}
                        >
                          Adjust
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {filteredStockLevels.length} of {stockLevels.length} records
          </div>
          <div>
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="mx-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StockLevelsPage;
